import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Checkbox,
} from '@mui/material';
import { Add, Delete, Publish, VisibilityOff, UploadFile } from '@mui/icons-material';
import { trainingService } from '../services/trainingService';
import TrainingSignatureDetailDialog from '../components/TrainingSignatureDetailDialog';
import TrainingFieldPlacementEditor from '../components/TrainingFieldPlacementEditor';
import { employeeService } from '../services/employeeService';
import { formatDateDisplay, formatLocalTodayYmd } from '../utils/dateUtils';

const emptyItem = () => ({ label: '', is_required: true });

const titleFromFileName = (name) => name.replace(/\.pdf$/i, '').trim() || name;

const TrainingDocuments = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadEffectiveDate, setUploadEffectiveDate] = useState(formatLocalTodayYmd());
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  const resetUploadForm = () => {
    setUploadQueue([]);
    setUploadDescription('');
    setUploadEffectiveDate(formatLocalTodayYmd());
    setUploadProgress('');
  };

  const addFilesToQueue = (fileList) => {
    const files = Array.from(fileList || []).filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    );
    if (files.length === 0) return;
    setUploadQueue((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        title: titleFromFileName(file.name),
      })),
    ]);
  };

  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editEffectiveDate, setEditEffectiveDate] = useState('');
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceEffectiveDate, setReplaceEffectiveDate] = useState('');
  const [replaceRequireResign, setReplaceRequireResign] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [signatureDetail, setSignatureDetail] = useState(null);

  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [items, setItems] = useState([emptyItem()]);
  const [fields, setFields] = useState([]);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [assignmentMode, setAssignmentMode] = useState('all_active');
  const [targets, setTargets] = useState([]);
  const [requiresSupervisor, setRequiresSupervisor] = useState(true);
  const [completions, setCompletions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [saving, setSaving] = useState(false);

  const [newTargetType, setNewTargetType] = useState('department');
  const [newTargetValue, setNewTargetValue] = useState('');

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await trainingService.listDocuments();
      setDocuments(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const loadDetail = async (doc) => {
    setSelected(doc);
    setDetailTab(0);
    try {
      const [docRes, empRes] = await Promise.all([
        trainingService.getDocument(doc.id),
        employeeService.getAll({ activeOnly: 'true', requiresTrainingOnly: 'true', limit: 500 }),
      ]);
      const data = docRes.data;
      setEditTitle(data.title || '');
      setEditDescription(data.description || '');
      setEditEffectiveDate(data.effective_date ? data.effective_date.slice(0, 10) : formatLocalTodayYmd());
      setReplaceEffectiveDate(data.effective_date ? data.effective_date.slice(0, 10) : formatLocalTodayYmd());
      setItems(data.items?.length ? data.items : [emptyItem()]);
      setFields(data.fields || []);
      setAssignmentMode(data.assignment_mode || 'all_active');
      setTargets(
        (data.targets || []).filter(
          (t, i, arr) =>
            arr.findIndex(
              (x) =>
                x.target_type === t.target_type &&
                String(x.target_value) === String(t.target_value)
            ) === i
        )
      );
      setRequiresSupervisor(data.requires_supervisor_signoff !== false);

      const emps = empRes.data || [];
      setEmployees(emps);
      setDepartments([...new Set(emps.map((e) => e.department).filter(Boolean))].sort());
      setJobTitles([...new Set(emps.map((e) => e.job_title).filter(Boolean))].sort());

      const compRes = await trainingService.getCompletions(doc.id);
      setCompletions(compRes.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load document detail');
    }
  };

  const handleUpload = async () => {
    const queue = uploadQueue.filter((item) => item.title.trim());
    if (queue.length === 0) return;
    setUploading(true);
    setError('');
    const failed = [];
    for (let i = 0; i < queue.length; i += 1) {
      const item = queue[i];
      setUploadProgress(`Uploading ${i + 1} of ${queue.length}…`);
      try {
        await trainingService.uploadDocument({
          file: item.file,
          title: item.title.trim(),
          description: uploadDescription.trim(),
          effectiveDate: uploadEffectiveDate || formatLocalTodayYmd(),
          assignmentMode: 'all_active',
          requiresSupervisorSignoff: true,
        });
      } catch (err) {
        failed.push({
          id: item.id,
          name: item.file.name,
          message: err.response?.data?.message || err.message || 'Upload failed',
        });
      }
    }
    setUploading(false);
    setUploadProgress('');
    await loadDocuments();
    if (failed.length === 0) {
      setUploadOpen(false);
      resetUploadForm();
      return;
    }
    const failedIds = new Set(failed.map((f) => f.id));
    setUploadQueue((prev) => prev.filter((item) => failedIds.has(item.id)));
    if (failed.length === queue.length) {
      setError(failed.map((f) => `${f.name}: ${f.message}`).join(' '));
    } else {
      setError(
        `Uploaded ${queue.length - failed.length} of ${queue.length}. Failed: ${failed.map((f) => f.name).join(', ')}`
      );
    }
  };

  const saveDetails = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await trainingService.updateDocument(selected.id, {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        effective_date: editEffectiveDate || null,
      });
      await loadDocuments();
      await loadDetail({ ...selected, title: editTitle.trim() });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save details');
    } finally {
      setSaving(false);
    }
  };

  const handleReplacePdf = async () => {
    if (!selected || !replaceFile) return;
    setSaving(true);
    try {
      await trainingService.replaceDocumentFile(selected.id, {
        file: replaceFile,
        effectiveDate: replaceEffectiveDate || undefined,
        requireResign: replaceRequireResign,
      });
      setReplaceOpen(false);
      setReplaceFile(null);
      await loadDocuments();
      await loadDetail(selected);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to replace PDF');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await trainingService.deleteDocument(selected.id);
      setDeleteOpen(false);
      setSelected(null);
      await loadDocuments();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete document');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!selected) {
      setPdfBlob(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const blob = await trainingService.downloadDocumentPdf(selected.id);
        if (!cancelled) setPdfBlob(blob);
      } catch {
        if (!cancelled) setPdfBlob(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.id]);

  const saveItems = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await trainingService.updateItems(selected.id, items.filter((i) => i.label?.trim()));
      await loadDetail(selected);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save checklist');
    } finally {
      setSaving(false);
    }
  };

  const saveFields = async (nextFields) => {
    if (!selected) return;
    setSaving(true);
    try {
      await trainingService.updateFields(selected.id, nextFields);
      await loadDetail(selected);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save PDF fields');
    } finally {
      setSaving(false);
    }
  };

  const saveAssignment = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await trainingService.updateDocument(selected.id, {
        assignment_mode: assignmentMode,
        requires_supervisor_signoff: requiresSupervisor,
      });
      if (assignmentMode === 'targeted') {
        await trainingService.updateTargets(selected.id, targets);
      } else {
        await trainingService.updateTargets(selected.id, []);
      }
      await loadDetail(selected);
      await loadDocuments();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save assignment');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (doc) => {
    setError('');
    try {
      await trainingService.updateDocument(doc.id, { is_published: !doc.is_published });
      await loadDocuments();
      if (selected?.id === doc.id) {
        const updated = { ...doc, is_published: !doc.is_published };
        setSelected(updated);
        await loadDetail(updated);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update publish status');
    }
  };

  const downloadCompleted = async (completion) => {
    setError('');
    try {
      const blob = await trainingService.downloadCompletedPdf(completion.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to download completed document');
    }
  };

  const addTarget = () => {
    if (!newTargetValue) return;
    const next = { target_type: newTargetType, target_value: String(newTargetValue) };
    setTargets((prev) => {
      const exists = prev.some(
        (t) =>
          t.target_type === next.target_type &&
          String(t.target_value) === String(next.target_value)
      );
      if (exists) return prev;
      return [...prev, next];
    });
    setNewTargetValue('');
  };

  const formatTargetLabel = (target) => {
    if (target.target_type === 'employee') {
      const emp = employees.find((e) => String(e.id) === String(target.target_value));
      if (emp) {
        const name = [emp.first_name, emp.last_name].filter(Boolean).join(' ').trim();
        const num = emp.employee_number ? ` (#${emp.employee_number})` : '';
        return `Employee: ${name || `ID ${target.target_value}`}${num}`;
      }
      return `Employee: ID ${target.target_value}`;
    }
    if (target.target_type === 'job_title') {
      return `Job title: ${target.target_value}`;
    }
    if (target.target_type === 'department') {
      return `Department: ${target.target_value}`;
    }
    return `${target.target_type}: ${target.target_value}`;
  };

  const openTargetEmployees = (target) => {
    if (target.target_type === 'job_title') {
      navigate(`/hr?job_title=${encodeURIComponent(target.target_value)}`);
      return;
    }
    if (target.target_type === 'department') {
      navigate(`/hr?department=${encodeURIComponent(target.target_value)}`);
      return;
    }
    if (target.target_type === 'employee') {
      navigate(`/hr?employee_id=${encodeURIComponent(target.target_value)}`);
    }
  };

  const completionStatusLabel = (status) => {
    if (status === 'pending') return 'Pending';
    if (status === 'employee_signed') return 'Awaiting supervisor';
    return 'Complete';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Training Documents
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { resetUploadForm(); setUploadOpen(true); }}>
          Upload PDFs
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Version date</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>Assignment</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No training documents yet.</TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow key={doc.id} hover selected={selected?.id === doc.id}>
                    <TableCell>{doc.title}</TableCell>
                    <TableCell>{doc.effective_date ? formatDateDisplay(doc.effective_date) : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={doc.is_published ? 'Published' : 'Draft'}
                        color={doc.is_published ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{doc.assignment_mode === 'targeted' ? 'Targeted' : 'All training-required'}</TableCell>
                    <TableCell>
                      {doc.completed_count ?? 0} / {doc.completion_count ?? 0} complete
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => loadDetail(doc)}>Manage</Button>
                      <IconButton
                        size="small"
                        title={doc.is_published ? 'Unpublish' : 'Publish'}
                        onClick={() => togglePublish(doc)}
                      >
                        {doc.is_published ? <VisibilityOff /> : <Publish />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {selected && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>{selected.title}</Typography>
          <Button
            size="small"
            sx={{ mb: 1 }}
            onClick={async () => {
              try {
                const blob = await trainingService.downloadDocumentPdf(selected.id);
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank', 'noopener,noreferrer');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (err) {
                setError(err.response?.data?.message || err.message || 'Failed to open PDF');
              }
            }}
          >
            View PDF
          </Button>
          <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 2 }}>
            <Tab label="Details" />
            <Tab label="Checklist" />
            <Tab label="PDF Fields" />
            <Tab label="Assignment" />
            <Tab label="Compliance" />
          </Tabs>

          {detailTab === 0 && (
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Description (optional)"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Version / effective date"
                type="date"
                value={editEffectiveDate}
                onChange={(e) => setEditEffectiveDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Use this date to label document versions. Employees who sign will have this date recorded."
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Button variant="contained" size="small" disabled={saving || !editTitle.trim()} onClick={saveDetails}>
                  {saving ? 'Saving…' : 'Save details'}
                </Button>
                <Button variant="outlined" size="small" startIcon={<UploadFile />} onClick={() => setReplaceOpen(true)}>
                  Replace PDF
                </Button>
                <Button variant="outlined" color="error" size="small" onClick={() => setDeleteOpen(true)}>
                  Delete document
                </Button>
              </Box>
            </Box>
          )}

          {detailTab === 1 && (
            <Box>
              {items.map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Item ${idx + 1}`}
                    value={item.label}
                    onChange={(e) => {
                      const next = [...items];
                      next[idx] = { ...next[idx], label: e.target.value };
                      setItems(next);
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setItems(items.filter((_, i) => i !== idx))}
                    disabled={items.length <= 1}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<Add />} onClick={() => setItems([...items, emptyItem()])} sx={{ mr: 1 }}>
                Add item
              </Button>
              <Button variant="contained" size="small" disabled={saving} onClick={saveItems}>
                {saving ? 'Saving…' : 'Save checklist'}
              </Button>
            </Box>
          )}

          {detailTab === 2 && (
            <TrainingFieldPlacementEditor
              pdfBlob={pdfBlob}
              fields={fields}
              onChange={setFields}
              onSave={saveFields}
              saving={saving}
            />
          )}

          {detailTab === 3 && (
            <Box>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Assignment</InputLabel>
                <Select
                  value={assignmentMode}
                  label="Assignment"
                  onChange={(e) => setAssignmentMode(e.target.value)}
                >
                  <MenuItem value="all_active">All training-required employees</MenuItem>
                  <MenuItem value="targeted">Targeted (department, job title, or individual)</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Only employees marked &quot;Required to sign training / onboarding documents&quot; in HR are assigned.
                Uncheck that flag on test or non-production accounts to exclude them from counts.
              </Typography>
              <FormControlLabel
                control={
                  <Switch checked={requiresSupervisor} onChange={(e) => setRequiresSupervisor(e.target.checked)} />
                }
                label="Requires supervisor sign-off"
                sx={{ mb: 2, display: 'block' }}
              />

              {assignmentMode === 'targeted' && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Type</InputLabel>
                      <Select value={newTargetType} label="Type" onChange={(e) => { setNewTargetType(e.target.value); setNewTargetValue(''); }}>
                        <MenuItem value="department">Department</MenuItem>
                        <MenuItem value="job_title">Job title</MenuItem>
                        <MenuItem value="employee">Employee</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                      <InputLabel>Value</InputLabel>
                      <Select value={newTargetValue} label="Value" onChange={(e) => setNewTargetValue(e.target.value)}>
                        {newTargetType === 'department' && departments.map((d) => (
                          <MenuItem key={d} value={d}>{d}</MenuItem>
                        ))}
                        {newTargetType === 'job_title' && jobTitles.map((t) => (
                          <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                        {newTargetType === 'employee' && employees.map((e) => (
                          <MenuItem key={e.id} value={String(e.id)}>
                            {e.first_name} {e.last_name} (#{e.employee_number})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={addTarget}>Add</Button>
                  </Box>
                  {targets.map((t, idx) => (
                    <Chip
                      key={`${t.target_type}-${t.target_value}-${idx}`}
                      clickable
                      label={formatTargetLabel(t)}
                      onClick={() => openTargetEmployees(t)}
                      onDelete={() => setTargets(targets.filter((_, i) => i !== idx))}
                      title="View matching employees"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                  {targets.length > 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Click a chip to open Employees filtered to that target. Use × to remove.
                    </Typography>
                  ) : null}
                </Box>
              )}

              <Button variant="contained" size="small" disabled={saving} onClick={saveAssignment}>
                {saving ? 'Saving…' : 'Save assignment'}
              </Button>
            </Box>
          )}

          {detailTab === 4 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Version signed</TableCell>
                    <TableCell>Employee signed</TableCell>
                    <TableCell>Supervisor signed</TableCell>
                    <TableCell align="right">Signatures</TableCell>
                    <TableCell align="right">Completed PDF</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {completions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No completions yet. Publish the document to assign employees.</TableCell>
                    </TableRow>
                  ) : (
                    completions.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.first_name} {c.last_name}</TableCell>
                        <TableCell>{completionStatusLabel(c.status)}</TableCell>
                        <TableCell>
                          {c.document_effective_date
                            ? formatDateDisplay(c.document_effective_date)
                            : (c.document_current_effective_date
                              ? formatDateDisplay(c.document_current_effective_date)
                              : '—')}
                        </TableCell>
                        <TableCell>{c.employee_signed_at ? formatDateDisplay(c.employee_signed_at) : '—'}</TableCell>
                        <TableCell>{c.supervisor_signed_at ? formatDateDisplay(c.supervisor_signed_at) : '—'}</TableCell>
                        <TableCell align="right">
                          {c.employee_signed_at && (
                            <Button
                              size="small"
                              sx={{ mr: 0.5 }}
                              onClick={() => setSignatureDetail({ completion: c, which: 'employee' })}
                            >
                              Employee
                            </Button>
                          )}
                          {c.supervisor_signed_at && (
                            <Button
                              size="small"
                              onClick={() => setSignatureDetail({ completion: c, which: 'supervisor' })}
                            >
                              Supervisor
                            </Button>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {c.status === 'supervisor_signed' ? (
                            <Button size="small" variant="outlined" onClick={() => downloadCompleted(c)}>
                              Download
                            </Button>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog
        open={uploadOpen}
        onClose={() => {
          if (!uploading) {
            setUploadOpen(false);
            resetUploadForm();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload training documents</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Description (optional, applies to all)"
            value={uploadDescription}
            onChange={(e) => setUploadDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Version / effective date (optional, applies to all)"
            type="date"
            value={uploadEffectiveDate}
            onChange={(e) => setUploadEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <Button variant="outlined" component="label" fullWidth disabled={uploading}>
            {uploadQueue.length > 0 ? 'Add more PDFs' : 'Choose PDFs'}
            <input
              type="file"
              hidden
              multiple
              accept="application/pdf,.pdf"
              onChange={(e) => {
                addFilesToQueue(e.target.files);
                e.target.value = '';
              }}
            />
          </Button>

          {uploadQueue.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {uploadQueue.length} file{uploadQueue.length === 1 ? '' : 's'} selected
              </Typography>
              {uploadQueue.map((item) => (
                <Box key={item.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={item.file.name}
                    value={item.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      setUploadQueue((prev) => prev.map((q) => (q.id === item.id ? { ...q, title } : q)));
                    }}
                    disabled={uploading}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setUploadQueue((prev) => prev.filter((q) => q.id !== item.id))}
                    disabled={uploading}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {uploadProgress && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {uploadProgress}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadOpen(false); resetUploadForm(); }} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={uploading || uploadQueue.length === 0 || uploadQueue.some((q) => !q.title.trim())}
            onClick={handleUpload}
          >
            {uploading
              ? 'Uploading…'
              : `Upload ${uploadQueue.length} document${uploadQueue.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={replaceOpen} onClose={() => !saving && setReplaceOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Replace PDF</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a new PDF for this document. Update the version date so you can track what employees signed.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Version / effective date"
            type="date"
            value={replaceEffectiveDate}
            onChange={(e) => setReplaceEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={replaceRequireResign}
                onChange={(e) => setReplaceRequireResign(e.target.checked)}
              />
            }
            label="Require all assigned employees to sign again"
            sx={{ mb: 2, display: 'block' }}
          />
          <Button variant="outlined" component="label" fullWidth>
            {replaceFile ? replaceFile.name : 'Choose new PDF'}
            <input
              type="file"
              hidden
              accept="application/pdf,.pdf"
              onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" disabled={saving || !replaceFile} onClick={handleReplacePdf}>
            {saving ? 'Replacing…' : 'Replace PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete training document?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete <strong>{selected?.title}</strong> and all sign-off records. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" disabled={saving} onClick={handleDelete}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <TrainingSignatureDetailDialog
        open={!!signatureDetail}
        onClose={() => setSignatureDetail(null)}
        completion={signatureDetail?.completion}
        which={signatureDetail?.which}
      />
    </Box>
  );
};

export default TrainingDocuments;
